/**
 * Números no idioma da sessão. Em português o canvas escreve handicap como "14,2" e differential
 * como "11,0" — vírgula decimal, sempre com a casa final; em inglês, ponto.
 *
 * The em dash is load-bearing: canvas 1a states "Nulo vira '—'. A ausência é informação."
 * A null putt count is not zero putts, and rendering it as 0 would be a lie.
 */

import { intlTag, t, type PlainMessageKey } from '@/lib/i18n';

export const EM_DASH = '—';

/**
 * Construídos uma vez, no topo do módulo. Continua correto porque **o idioma é congelado no boot**:
 * há um `intlTag` por vida de página, então não existe o que memoizar por idioma.
 */
const nf = (min: number, max: number) =>
  new Intl.NumberFormat(intlTag, { minimumFractionDigits: min, maximumFractionDigits: max });

const nf0 = nf(0, 0);
const nf1 = nf(1, 1);
const nf2 = nf(2, 2);

/** Whole number, or "—" when absent. */
export function fmtInt(n: number | null | undefined): string {
  return n == null ? EM_DASH : nf0.format(n);
}

/** One decimal — handicap index, score differential, putts per round. */
export function fmtDecimal(n: number | null | undefined): string {
  return n == null ? EM_DASH : nf1.format(n);
}

/** Two decimals — average putts per hole. */
export function fmtDecimal2(n: number | null | undefined): string {
  return n == null ? EM_DASH : nf2.format(n);
}

/** Percentage as an integer, e.g. 33%. */
export function fmtPct(n: number | null | undefined): string {
  return n == null ? EM_DASH : `${nf0.format(Math.round(n))}%`;
}

/**
 * Score relative to par, always signed. Level par is "E" (even), which is how
 * scorecards have always written it — "+0" would read as a typo.
 */
export function fmtToPar(n: number | null | undefined): string {
  if (n == null) return EM_DASH;
  if (n === 0) return 'E';
  return n > 0 ? `+${nf0.format(n)}` : nf0.format(n);
}

/** Signed delta for handicap movement, e.g. "▼ 0,4". Lower is better in golf. */
export function fmtHandicapDelta(delta: number | null | undefined): string | null {
  if (delta == null || delta === 0) return null;
  return `${delta < 0 ? '▼' : '▲'} ${nf1.format(Math.abs(delta))}`;
}

/**
 * Ordinal: "3º" em português, "3rd" em inglês.
 *
 * Português usa um sufixo só; inglês usa quatro, escolhidos por uma regra que **não** é "o último
 * dígito" — 11, 12 e 13 fogem dela. Quem sabe a regra é o `Intl.PluralRules` em modo ordinal, e o
 * catálogo só guarda o sufixo de cada categoria.
 */
const ORDINAL = new Intl.PluralRules(intlTag, { type: 'ordinal' });

const ORDINAL_SUFFIX: Record<string, PlainMessageKey> = {
  one: 'format.ordinal.one',
  two: 'format.ordinal.two',
  few: 'format.ordinal.few',
  other: 'format.ordinal.other',
};

export function fmtOrdinal(n: number): string {
  const key = ORDINAL_SUFFIX[ORDINAL.select(n)] ?? 'format.ordinal.other';
  return `${nf0.format(n)}${t(key)}`;
}

/** "6 de 18" — the sample behind a percentage. */
export function fmtOutOf(hit: number, total: number): string {
  return t('format.outOf', { hit: nf0.format(hit), total: nf0.format(total) });
}
